import React, { useEffect } from "react";
import HeroFR from "@/components/landing-fr/HeroFR";
import SectionHeaderFR from "@/components/landing-fr/SectionHeaderFR";
import PrimaryCardsFR from "@/components/landing-fr/PrimaryCardsFR";
import PricingFR from "@/components/landing-fr/PricingFR";
import PitchMeetingFR from "@/components/landing-fr/PitchMeetingFR";
import FounderQuoteFR from "@/components/landing-fr/FounderQuoteFR";
import SiteFooterFR from "@/components/landing-fr/SiteFooterFR";
import { fr } from "@/locales/fr";
import registry from "@/locales/registry.json";

const SITE_ORIGIN = "https://voxiumscript.com";
// Path comes from the registry, not a hardcoded string — "/fr/" with
// trailing slash, matching the form that serves directly (200, no
// redirect) from the production static build (see generate-fr-shell.js).
const FR_URL = `${SITE_ORIGIN}${registry.find((e) => e.code === "fr").path}`;

/**
 * French homepage — full mirrored route at /fr. English homepage
 * (LandingPage.jsx) is untouched; this is a separate, additive page.
 *
 * Head management mirrors the pattern already used by LegalPages.jsx
 * (mutate the existing shell's <title> / canonical on mount, restore on
 * unmount) and extends it to <html lang>, meta description, and the
 * JSON-LD script's `description`/`url` fields. The JSON-LD `offers`
 * array is read from whatever is already in the DOM and left untouched,
 * so French offers can never drift from the English ones — there is
 * only ever one offers array, mutated in place for two fields only.
 *
 * hreflang alternates are NOT touched here: they are static, identical
 * on every route (generated once into public/index.html from the locale
 * registry), so no runtime mutation is needed for them.
 */
const LandingPageFR = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal:not(.is-visible)");
    if (!els.length || typeof IntersectionObserver === "undefined") {
      els.forEach((el) => el.classList.add("is-visible"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const prevLang = root.getAttribute("lang");
    root.setAttribute("lang", "fr");

    const prevTitle = document.title;
    document.title = fr.head.title;

    const descMeta = document.querySelector('meta[name="description"]');
    const prevDesc = descMeta ? descMeta.getAttribute("content") : null;
    if (descMeta) descMeta.setAttribute("content", fr.head.description);

    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute("href") : null;
    if (canonical) canonical.setAttribute("href", FR_URL);

    // og:*/twitter:* content-swaps — same mutate-on-mount/restore-on-
    // unmount shape for each. og:image is deliberately excluded (brief:
    // "Do NOT localize the OG card image").
    const metaSwaps = [
      { selector: 'meta[property="og:title"]', value: fr.head.title },
      { selector: 'meta[property="og:description"]', value: fr.head.description },
      { selector: 'meta[property="og:url"]', value: FR_URL },
      { selector: 'meta[name="twitter:title"]', value: fr.head.title },
      { selector: 'meta[name="twitter:description"]', value: fr.head.description },
      { selector: 'meta[name="twitter:url"]', value: FR_URL },
    ].map(({ selector, value }) => {
      const el = document.querySelector(selector);
      const prevValue = el ? el.getAttribute("content") : null;
      if (el) el.setAttribute("content", value);
      return { el, prevValue };
    });

    // og:locale doesn't exist on the English shell at all — create it
    // for French, remove it (not "restore to absent", literally remove
    // the node) on unmount so English never gains the tag.
    let ogLocale = document.querySelector('meta[property="og:locale"]');
    const ogLocaleExisted = !!ogLocale;
    if (!ogLocale) {
      ogLocale = document.createElement("meta");
      ogLocale.setAttribute("property", "og:locale");
      document.head.appendChild(ogLocale);
    }
    const prevOgLocale = ogLocaleExisted ? ogLocale.getAttribute("content") : null;
    ogLocale.setAttribute("content", "fr_FR");

    const jsonLdScript = document.querySelector('script[type="application/ld+json"]');
    const prevJsonLd = jsonLdScript ? jsonLdScript.textContent : null;
    if (jsonLdScript) {
      try {
        const data = JSON.parse(jsonLdScript.textContent);
        data.description = fr.head.description;
        data.url = FR_URL;
        jsonLdScript.textContent = JSON.stringify(data, null, 2);
      } catch {
        // Malformed JSON-LD in the shell — leave it untouched rather
        // than break the page over a non-critical metadata block.
      }
    }

    return () => {
      if (prevLang !== null) root.setAttribute("lang", prevLang);
      else root.removeAttribute("lang");
      document.title = prevTitle;
      if (descMeta && prevDesc !== null) descMeta.setAttribute("content", prevDesc);
      if (canonical && prevCanonical !== null) canonical.setAttribute("href", prevCanonical);
      metaSwaps.forEach(({ el, prevValue }) => {
        if (el && prevValue !== null) el.setAttribute("content", prevValue);
      });
      if (ogLocaleExisted) {
        if (prevOgLocale !== null) ogLocale.setAttribute("content", prevOgLocale);
      } else {
        ogLocale.remove();
      }
      if (jsonLdScript && prevJsonLd !== null) jsonLdScript.textContent = prevJsonLd;
    };
  }, []);

  return (
    <main data-testid="landing-page-fr" className="bg-cream" lang="fr">
      <HeroFR />
      <SectionHeaderFR />
      <PrimaryCardsFR />
      <PricingFR />
      <PitchMeetingFR />
      <FounderQuoteFR />
      <SiteFooterFR />
    </main>
  );
};

export default LandingPageFR;
